import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/state/store';
import { setUploadBatch } from '@/state/translationFlowSlice';
import { useActiveWorkflow } from './useActiveWorkflow';
import { PageLoader } from '@/shared/components/PageLoader';

/**
 * Guards the /upload route. While a workflow is active, sets the batch id in the
 * flow slice (so the target screen can cold-start) and redirects to the stage's
 * screen. Renders the upload page only when the user is free.
 */
export function RequireNoActiveWorkflow({ children }: Readonly<{ children: ReactNode }>) {
  const dispatch = useDispatch<AppDispatch>();
  const { activeWorkflow, stageRoute, isLoading, isFetching, isUninitialized } = useActiveWorkflow();
  const uploadBatchId = useSelector((s: RootState) => s.translationFlow.uploadBatchId);

  useEffect(() => {
    if (activeWorkflow) dispatch(setUploadBatch(activeWorkflow.batchUploadId));
  }, [activeWorkflow, dispatch]);

  // Don't act until /me has settled: cover the pre-auth skip window
  // (isUninitialized) and first load (isLoading), plus a stale workflow being
  // refetched. (Belt-and-braces: this guard mounts inside RequireAuth, but the
  // isUninitialized check keeps it correct even if that ever changes.)
  if (isUninitialized || isLoading || (activeWorkflow && isFetching)) return <PageLoader />;

  if (activeWorkflow && stageRoute) {
    // Redirect only once the flow slice reflects the batch, so the target screen
    // rebuilds from it — prevents a self-redirect bounce off the stage page.
    return uploadBatchId === activeWorkflow.batchUploadId
      ? <Navigate to={stageRoute} replace />
      : <PageLoader />;
  }

  return <>{children}</>;
}
